import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Formik, useFormik } from 'formik';
import * as Yup from 'yup';
import { TextField, Checkbox, FormControlLabel, Button, Container, Box, Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { Autocomplete } from '@material-ui/lab';
import { getListeFournisseur, getListePierre } from '../../actions';
import { getPierre } from '../../selectors/pierre';
import { getTodaySqlFormat } from '../../helpers/functions';
import Axios from 'axios';
import { apiATC } from '../../config';

// Helper for Material-UI Alert
function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// Validation schema using Yup
const validationSchema = Yup.object().shape({
    confie: Yup.string().required('N° Confié obligatoire.'),
    fournisseur: Yup.object().required('Fournisseur obligatoire.').nullable(),
    pierre: Yup.object().required('Pierre obligatoire.').nullable(),
    dollar: Yup.number().typeError('Le prix $ doit être un nombre.').nullable(true),
    euro: Yup.number().typeError('Le prix € doit être un nombre.').nullable(true),
    poids: Yup.number().required('Le poids est obligatoire.').typeError('Le poids doit être un nombre.'),
    euroDollar: Yup.number().typeError('Le cours €/$ doit être un nombre.'),
    // --- ADDED THIS LINE ---
    certificat: Yup.string().nullable(), // Certificat is optional, but still part of the schema
});

const ReceptionPierre = ({ listeFournisseur, getListeFournisseur, getListePierre, listePierre }) => {
    // Refs for input focus management
    const confieRef = useRef(null);
    const fournisseurRef = useRef(null);
    const pierreRef = useRef(null);
    const dollarRef = useRef(null);
    const euroRef = useRef(null);
    const lgRef = useRef(null);
    const poidsRef = useRef(null);
    const euroDollarRef = useRef(null);
    const certificatRef = useRef(null);

    // State for controlled components and Snackbar
    const [confieValue, setConfieValue] = useState('');
    const [fournisseurValue, setFournisseurValue] = useState(null);
    const [euroDollarValue, setEuroDollarValue] = useState('1');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Formik hook for form management and validation
    const formik = useFormik({
        initialValues: {
            confie: confieValue,
            euro: '',
            dollar: '',
            poids: '',
            certificat: '',
            euroDollar: euroDollarValue,
            fournisseur: fournisseurValue,
            pierre: null,
            lg: false,
        },
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            if (!values.certificat) {
                setSnackbarMessage('Attention : Le champ Certificat est vide. L\'enregistrement va continuer.');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
            }

            const data = {
                table: 'pierres_recues',
                no_confie: values.confie,
                date_reception: getTodaySqlFormat(),
                id_four: values.fournisseur?.four_id,
                id_pier: values.pierre?.id_pier,
                id_qual: values.pierre?.id_qual,
                euro: values.euro,
                dollar: values.dollar,
                cours_eur_doll: values.euroDollar,
                poids: values.poids,
                certificat: values.certificat,
                lg: values.lg ? 1 : 0,
            };

            Axios.post(`${apiATC}/general.php?action=ajouter`, data)
                .then(() => {
                    setSnackbarMessage('Pierre enregistrée avec succès !');
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                    resetForm({
                        values: {
                            ...values,
                            euro: '',
                            dollar: '',
                            poids: '',
                            certificat: '',
                            pierre: null,
                            lg: false,
                        }
                    });
                    if (pierreRef.current) {
                        pierreRef.current.focus();
                    } else if (confieRef.current) {
                        confieRef.current.focus();
                    }
                })
                .catch((error) => {
                    console.error('Erreur lors de l\'enregistrement de la pierre :', error);
                    setSnackbarMessage('Erreur lors de l\'enregistrement de la pierre. Veuillez réessayer.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                });
        },
    });

    const { values, errors, touched, handleChange, handleSubmit, handleBlur, setFieldValue, isValid, dirty } = formik;

    useEffect(() => {
        getListeFournisseur();
        getListePierre();
    }, [getListeFournisseur, getListePierre]);

    useEffect(() => {
        if (values.fournisseur && pierreRef.current) {
            pierreRef.current.focus();
        }
    }, [values.fournisseur]);

    useEffect(() => {
        if (values.pierre && dollarRef.current) {
            dollarRef.current.focus();
        }
    }, [values.pierre]);

    const filterOptions = (options, state) => {
        const inputValue = state.inputValue.toLowerCase();
        const inputParts = inputValue.split(' ');

        return options.filter(option => {
            const description = option.descriptionPierre.toLowerCase();
            return inputParts.every(part => description.includes(part));
        });
    };

    const handleKeyPress = async (e, fieldName, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            // Special handling for checkbox 'lg'
            if (fieldName === 'lg') {
                if (nextRef && nextRef.current) {
                    nextRef.current.focus();
                } else {
                    // If this is the last "navigable" element, attempt submission if valid
                    if (isValid && dirty) {
                        handleSubmit();
                    } else {
                        Object.keys(values).forEach(field => {
                            setFieldValue(field, values[field], true);
                        });
                        setSnackbarMessage('Veuillez remplir tous les champs obligatoires.');
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                    }
                }
                return; // Stop here for checkbox
            }

            // For other input types (TextField, Autocomplete)
            await formik.setFieldTouched(fieldName, true, true);
            await formik.validateField(fieldName);

            const fieldError = formik.errors[fieldName];
            const fieldTouched = formik.touched[fieldName];

            if (fieldError && fieldTouched) {
                setSnackbarMessage(`Veuillez corriger le champ '${fieldName}' avant de continuer.`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }

            // --- MODIFIED LOGIC FOR LAST FIELD (CERTIFICAT) ---
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            } else {
                // If there's no nextRef, it means this is the last field ('certificat')
                // We should now attempt to submit the form if it's generally valid and dirty
                if (isValid && dirty) {
                    handleSubmit();
                } else {
                    // If the form is not valid (even if this field is), show general errors
                    // Mark all fields as touched to display all validation messages
                    await formik.validateForm(); // Force validation of the entire form
                    Object.keys(values).forEach(field => {
                        setFieldValue(field, values[field], true);
                    });
                    setSnackbarMessage('Veuillez remplir tous les champs obligatoires.');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            }
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <Container maxWidth="md">
            <Box display="flex" justifyContent="center">
                <Box width="100%">
                    <Formik initialValues={values} onSubmit={handleSubmit}>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit(e);
                        }}>
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <TextField
                                        autoFocus
                                        required
                                        label="N° Confié"
                                        variant="outlined"
                                        value={values.confie}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setConfieValue(e.target.value);
                                        }}
                                        onBlur={handleBlur}
                                        name="confie"
                                        error={errors.confie && touched.confie}
                                        helperText={(errors.confie && touched.confie) ? errors.confie : null}
                                        inputRef={confieRef}
                                        onKeyPress={(e) => handleKeyPress(e, 'confie', fournisseurRef)}
                                        style={{ width: '48%' }}
                                    />
                                    <Box style={{ width: '48%' }}>
                                        <Autocomplete
                                            options={listeFournisseur}
                                            getOptionLabel={(option) => option.raison_soc || ''}
                                            value={values.fournisseur}
                                            onChange={(event, newValue) => {
                                                setFieldValue('fournisseur', newValue);
                                                setFournisseurValue(newValue);
                                            }}
                                            onBlur={() => handleBlur({ target: { name: 'fournisseur', value: values.fournisseur } })}
                                            autoHighlight
                                            autoComplete
                                            clearOnEscape
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    label="Fournisseur"
                                                    variant="outlined"
                                                    error={errors.fournisseur && touched.fournisseur}
                                                    helperText={(errors.fournisseur && touched.fournisseur) ? errors.fournisseur : null}
                                                    inputRef={fournisseurRef}
                                                    onKeyPress={(e) => handleKeyPress(e, 'fournisseur', pierreRef)}
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>
                                <Box mb={2}>
                                    <Autocomplete
                                        options={listePierre}
                                        getOptionLabel={(option) => option.descriptionPierre || ''}
                                        value={values.pierre}
                                        onChange={(event, newValue) => {
                                            setFieldValue('pierre', newValue);
                                            setFieldValue('euro', newValue?.euro || '');
                                            setFieldValue('dollar', newValue?.dollar || '');
                                        }}
                                        onBlur={() => handleBlur({ target: { name: 'pierre', value: values.pierre } })}
                                        filterOptions={filterOptions}
                                        autoHighlight
                                        autoComplete
                                        clearOnEscape
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Pierre"
                                                variant="outlined"
                                                error={errors.pierre && touched.pierre}
                                                helperText={(errors.pierre && touched.pierre) ? errors.pierre : null}
                                                inputRef={pierreRef}
                                                fullWidth
                                                onKeyPress={(e) => handleKeyPress(e, 'pierre', dollarRef)}
                                            />
                                        )}
                                    />
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <TextField
                                        label="Prix $"
                                        variant="outlined"
                                        value={values.dollar}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        name="dollar"
                                        error={errors.dollar && touched.dollar}
                                        helperText={(errors.dollar && touched.dollar) ? errors.dollar : null}
                                        inputRef={dollarRef}
                                        onKeyPress={(e) => handleKeyPress(e, 'dollar', euroRef)}
                                        style={{ width: '48%' }}
                                    />
                                    <TextField
                                        label="Prix €"
                                        variant="outlined"
                                        value={values.euro}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        name="euro"
                                        error={errors.euro && touched.euro}
                                        helperText={(errors.euro && touched.euro) ? errors.euro : null}
                                        inputRef={euroRef}
                                        onKeyPress={(e) => handleKeyPress(e, 'euro', poidsRef)}
                                        style={{ width: '48%' }}
                                    />
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <TextField
                                        label="Poids Reçu"
                                        variant="outlined"
                                        value={values.poids}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        name="poids"
                                        error={errors.poids && touched.poids}
                                        helperText={(errors.poids && touched.poids) ? errors.poids : null}
                                        inputRef={poidsRef}
                                        onKeyPress={(e) => handleKeyPress(e, 'poids', lgRef)}
                                        style={{ width: '48%' }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.lg}
                                                onChange={handleChange}
                                                name="lg"
                                                color="primary"
                                                inputRef={lgRef}
                                                onKeyPress={(e) => handleKeyPress(e, 'lg', euroDollarRef)}
                                            />
                                        }
                                        label="Pierre de Labo"
                                        style={{ width: '48%' }}
                                    />
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={2}>
                                    <TextField
                                        label="Cours €/$"
                                        variant="outlined"
                                        value={values.euroDollar}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setEuroDollarValue(e.target.value);
                                        }}
                                        onBlur={handleBlur}
                                        name="euroDollar"
                                        error={errors.euroDollar && touched.euroDollar}
                                        helperText={(errors.euroDollar && touched.euroDollar) ? errors.euroDollar : null}
                                        inputRef={euroDollarRef}
                                        onKeyPress={(e) => handleKeyPress(e, 'euroDollar', certificatRef)}
                                        style={{ width: '48%' }}
                                    />
                                    <TextField
                                        label="Certificat"
                                        variant="outlined"
                                        value={values.certificat}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        name="certificat"
                                        inputRef={certificatRef}
                                        onKeyPress={(e) => handleKeyPress(e, 'certificat', null)} // Last field, nextRef is null
                                        error={errors.certificat && touched.certificat} // Even if optional, it can still have type errors, etc.
                                        helperText={(errors.certificat && touched.certificat) ? errors.certificat : null}
                                        style={{ width: '48%' }}
                                    />
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="center">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={!isValid || !dirty}
                                >
                                    Enregistrer
                                </Button>
                            </Box>
                        </form>
                    </Formik>
                </Box>
            </Box>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

const mapStateToProps = (state) => ({
    listeFournisseur: state.generalData.listeFournisseur,
    listePierre: getPierre(state),
});

const mapDispatchToProps = {
    getListeFournisseur,
    getListePierre,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReceptionPierre);